import React, { useEffect, useState } from 'react'
import { CalendarDays } from 'lucide-react'
import { apiGet } from '../api'
import { useFacility } from '../context/FacilityContext'
import { DataTable, EmptyState, Loading, PageHeader, StatusPill } from '../components/ui'
import './pages.css'

function fmtDate(value) {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleString('nb-NO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch (_) {
    return String(value)
  }
}

// PADEL → Padel. Andre verdier vises som de er.
const prettySport = (s) => (s && s === s.toUpperCase() ? s.charAt(0) + s.slice(1).toLowerCase().replace(/_/g, ' ') : s)

const ACTIVITY_STATUS = {
  OPEN: { label: 'Åpen', tone: 'open' },
  ACTIVE: { label: 'Åpen', tone: 'open' },
  UPCOMING: { label: 'Åpen', tone: 'open' },
  FULL: { label: 'Fullt', tone: 'neutral' },
  ONGOING: { label: 'Pågår', tone: 'live' },
  IN_PROGRESS: { label: 'Pågår', tone: 'live' },
  STARTED: { label: 'Pågår', tone: 'live' },
  COMPLETED: { label: 'Fullført', tone: 'muted' },
  FINISHED: { label: 'Fullført', tone: 'muted' },
  CLOSED: { label: 'Stengt', tone: 'muted' },
  CANCELLED: { label: 'Avlyst', tone: 'danger' },
  CANCELED: { label: 'Avlyst', tone: 'danger' }
}

const statusOf = (a) => {
  const st = ACTIVITY_STATUS[(a.status || '').toUpperCase()]
  if (!st) return { label: a.status || '—', tone: 'neutral' }
  const count = a.currentPlayers ?? a.acceptedCount ?? a.participantCount
  if (st.tone === 'open' && a.maxPlayers != null && count != null) {
    const left = a.maxPlayers - count
    if (left <= 0) return { label: 'Fullt', tone: 'neutral' }
    if (left <= 2 && left / a.maxPlayers <= 0.2) return { label: 'Få plasser', tone: 'signal' }
  }
  return st
}

const COLUMNS = [
  { key: 'title', header: 'Tittel', render: (a) => <span className="cell-title">{a.title || a.name || '—'}</span> },
  {
    key: 'sport',
    header: 'Idrett',
    render: (a) => {
      const sport = prettySport(a.sportType || a.sport)
      if (!sport) return '—'
      return (
        <span className="sport-cell">
          <span className="sport-mono" aria-hidden="true">{sport.slice(0, 2)}</span>
          {sport}
        </span>
      )
    }
  },
  {
    key: 'time',
    header: 'Tidspunkt',
    className: 'cell-mono',
    render: (a) => fmtDate(a.startTime || a.startsAt || a.date || a.dateTime)
  },
  {
    key: 'count',
    header: 'Påmeldte',
    render: (a) => {
      const count = a.currentPlayers ?? a.acceptedCount ?? a.participantCount
      const pct = a.maxPlayers && count != null ? Math.min(100, Math.round((count / a.maxPlayers) * 100)) : null
      return (
        <span className="fill-cell">
          <span className="mono">
            {count ?? '—'}
            {a.maxPlayers != null ? ` / ${a.maxPlayers}` : ''}
          </span>
          {pct != null && (
            <span className="progress" aria-hidden="true"><span style={{ width: `${pct}%` }} /></span>
          )}
        </span>
      )
    }
  },
  {
    key: 'status',
    header: 'Status',
    render: (a) => {
      const st = statusOf(a)
      return <StatusPill tone={st.tone}>{st.label}</StatusPill>
    }
  }
]

function ActivitiesPage() {
  const { activeFacilityId } = useFacility()
  const [activities, setActivities] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!activeFacilityId) return
    const load = async () => {
      setLoading(true)
      try {
        const res = await apiGet(`/api/activities/facility/${activeFacilityId}?page=0&size=100`)
        if (res.ok) {
          const data = await res.json()
          setActivities(data.content || data || [])
        }
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [activeFacilityId])

  return (
    <div className="page">
      <PageHeader title="Aktiviteter" subtitle="Aktiviteter knyttet til anlegget." />
      {loading ? (
        <Loading />
      ) : activities.length === 0 ? (
        <EmptyState icon={CalendarDays} title="Ingen aktiviteter for dette anlegget." />
      ) : (
        <div className="tall-table">
          <DataTable columns={COLUMNS} rows={activities} minWidth={760} caption="Aktiviteter" />
        </div>
      )}
    </div>
  )
}

export default ActivitiesPage
